/**
 * Lazy-load HTML fragments into skeleton placeholder regions.
 *
 * Usage:
 * <div data-skeleton-region data-skeleton-url="/path" data-skeleton-event="my-loaded">
 *   <x-skeleton.media-grid />
 * </div>
 */

export async function loadSkeletonRegion(region) {
    const url = region.dataset.skeletonUrl;
    if (!url || region.dataset.skeletonLoaded === 'true') {
        return;
    }

    region.setAttribute('aria-busy', 'true');

    try {
        const response = await fetch(url, {
            headers: {
                Accept: 'text/html',
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
        });

        if (!response.ok) {
            throw new Error(`Request failed (${response.status})`);
        }

        region.innerHTML = await response.text();
        region.dataset.skeletonLoaded = 'true';

        const eventName = region.dataset.skeletonEvent;
        if (eventName) {
            document.dispatchEvent(new CustomEvent(eventName, { detail: { region } }));
        }

        region.dispatchEvent(new CustomEvent('skeleton-region:loaded', { bubbles: true }));
    } catch (error) {
        region.innerHTML = `
            <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Could not load this section. Please refresh the page.
            </div>
        `;
        console.error('Skeleton region failed to load', error);
    } finally {
        region.removeAttribute('aria-busy');
    }
}

export function initSkeletonRegions(root = document) {
    const regions = root.querySelectorAll('[data-skeleton-region]');
    regions.forEach((region) => {
        void loadSkeletonRegion(region);
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initSkeletonRegions();
});
